import React, { useState, useEffect, useCallback } from 'react';
import { usersApi } from '../services/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const ROLES = ['ADMIN', 'MANAGER', 'VIEWER', 'CLIENT'];

const ROLE_META = {
  ADMIN: {
    label: 'Administrator',
    icon: '👑',
    color: '#EA580C',
    bg: '#FFF7ED',
    border: '#FDBA74',
    desc: 'Full system control: users, audit logs, email flows, and security governance.',
  },
  MANAGER: {
    label: 'Operations Manager',
    icon: '👔',
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#93C5FD',
    desc: 'Manage report dispatches, distribution matrices, and team analytics.',
  },
  VIEWER: {
    label: 'Internal Viewer',
    icon: '👤',
    color: '#15803D',
    bg: '#F0FDF4',
    border: '#86EFAC',
    desc: 'Access assigned operational reports, spreadsheets, and personal dispatches.',
  },
  CLIENT: {
    label: 'Client / Partner',
    icon: '🤝',
    color: '#7C3AED',
    bg: '#FAF5FF',
    border: '#D8B4FE',
    desc: 'Restricted external view of designated vendor reports and files.',
  },
};

function UserModal({ user, onClose, onSave }) {
  const isEditing = !!user;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'VIEWER',
    isActive: user?.isActive ?? true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const initials = (form.name || user?.name || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const activeRoleMeta = ROLE_META[form.role] || ROLE_META.VIEWER;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEditing && !payload.password) delete payload.password;

      if (isEditing) {
        await usersApi.update(user.id, payload);
        toast.success(`User "${payload.name}" updated successfully`);
      } else {
        await usersApi.create(payload);
        toast.success(`User "${payload.name}" created successfully`);
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          'Failed to save user'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.72)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 9999,
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '560px',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(226, 232, 240, 0.8)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Top Role-Colored Accent Banner */}
        <div
          style={{
            height: '6px',
            background: `linear-gradient(90deg, ${activeRoleMeta.color} 0%, #00C0FA 100%)`,
            transition: 'background 0.3s ease',
          }}
        />

        {/* Modal Header */}
        <div
          style={{
            padding: '22px 26px 18px',
            borderBottom: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #FAFAFC 0%, #FFFFFF 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {/* Dynamic Avatar with Role Ring */}
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                background: `linear-gradient(135deg, ${activeRoleMeta.color} 0%, #0B132B 100%)`,
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '17px',
                fontWeight: '900',
                letterSpacing: '0.04em',
                boxShadow: `0 8px 16px ${activeRoleMeta.color}35`,
                position: 'relative',
                flexShrink: 0,
                transition: 'all 0.3s ease',
              }}
            >
              {initials}
              <span
                style={{
                  position: 'absolute',
                  bottom: '-3px',
                  right: '-3px',
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  background: form.isActive ? '#10B981' : '#EF4444',
                  border: '2.5px solid #FFFFFF',
                  boxShadow: '0 0 6px rgba(0,0,0,0.2)',
                }}
                title={form.isActive ? 'Active Account' : 'Inactive Account'}
              />
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '900',
                    color: '#0F172A',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {isEditing ? 'Edit User Profile' : 'Create New User'}
                </h3>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '800',
                    padding: '2px 8px',
                    borderRadius: '20px',
                    background: activeRoleMeta.bg,
                    color: activeRoleMeta.color,
                    border: `1px solid ${activeRoleMeta.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span>{activeRoleMeta.icon}</span>
                  <span>{form.role}</span>
                </span>
              </div>
              <div
                style={{
                  fontSize: '12.5px',
                  color: '#64748B',
                  marginTop: '3px',
                  fontWeight: '500',
                }}
              >
                {isEditing ? (
                  <span>
                    Account: <code style={{ color: '#0052CC', fontWeight: '700' }}>{user.email}</code>
                  </span>
                ) : (
                  'Provision team access credentials and permission scope'
                )}
              </div>
            </div>
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              background: '#F1F5F9',
              color: '#64748B',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: '700',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#E2E8F0';
              e.currentTarget.style.color = '#0F172A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#F1F5F9';
              e.currentTarget.style.color = '#64748B';
            }}
            title="Close dialog"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form
          onSubmit={handleSubmit}
          style={{
            padding: '22px 26px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
          }}
        >
          {/* Row 1: Full Name & Email */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11.5px',
                  fontWeight: '800',
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '6px',
                }}
              >
                Full Name <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '11px',
                    fontSize: '14px',
                    color: '#94A3B8',
                  }}
                >
                  👤
                </span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Naveen C"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px 10px 36px',
                    borderRadius: '10px',
                    border: '1.5px solid #CBD5E1',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    color: '#0F172A',
                    background: '#F8FAFC',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0052CC';
                    e.target.style.background = '#FFFFFF';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#CBD5E1';
                    e.target.style.background = '#F8FAFC';
                  }}
                />
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11.5px',
                  fontWeight: '800',
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '6px',
                }}
              >
                Email Address <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '11px',
                    fontSize: '14px',
                    color: '#94A3B8',
                  }}
                >
                  ✉️
                </span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="name@kaizeninfoserve.com"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px 10px 36px',
                    borderRadius: '10px',
                    border: '1.5px solid #CBD5E1',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    color: '#0F172A',
                    background: '#F8FAFC',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0052CC';
                    e.target.style.background = '#FFFFFF';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#CBD5E1';
                    e.target.style.background = '#F8FAFC';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Row 2: Password with Eye Toggle */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label
                style={{
                  fontSize: '11.5px',
                  fontWeight: '800',
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Password {isEditing ? <span style={{ color: '#64748B', fontWeight: '500' }}>(Optional)</span> : <span style={{ color: '#EF4444' }}>*</span>}
              </label>
              {isEditing && (
                <span style={{ fontSize: '11px', color: '#64748B', fontStyle: 'italic' }}>
                  Leave blank to retain current password
                </span>
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '11px',
                  fontSize: '14px',
                  color: '#94A3B8',
                }}
              >
                🔒
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={isEditing ? '••••••••••••  (Unchanged)' : 'Enter initial password (min. 8 characters)'}
                minLength={isEditing ? 0 : 8}
                required={!isEditing}
                style={{
                  width: '100%',
                  padding: '10px 42px 10px 36px',
                  borderRadius: '10px',
                  border: '1.5px solid #CBD5E1',
                  fontSize: '13.5px',
                  fontWeight: '600',
                  color: '#0F172A',
                  background: '#F8FAFC',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0052CC';
                  e.target.style.background = '#FFFFFF';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#CBD5E1';
                  e.target.style.background = '#F8FAFC';
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '8px',
                  background: 'none',
                  border: 'none',
                  color: '#64748B',
                  cursor: 'pointer',
                  fontSize: '15px',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {isEditing && form.password && (
              <div style={{ fontSize: '11px', color: '#EA580C', fontWeight: '700', marginTop: '4px' }}>
                ⚠️ Password will be updated to the new value upon saving.
              </div>
            )}
          </div>

          {/* Row 3: Interactive Role Selection Cards */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '11.5px',
                fontWeight: '800',
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '8px',
              }}
            >
              Access Role & Permission Level <span style={{ color: '#EF4444' }}>*</span>
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {ROLES.map((role) => {
                const meta = ROLE_META[role];
                const isSelected = form.role === role;
                return (
                  <div
                    key={role}
                    onClick={() => setForm({ ...form, role })}
                    style={{
                      border: isSelected ? `2px solid ${meta.color}` : '1.5px solid #E2E8F0',
                      borderRadius: '12px',
                      padding: '12px 14px',
                      background: isSelected ? meta.bg : '#FAFAFC',
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      boxShadow: isSelected ? `0 4px 14px ${meta.color}25` : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#CBD5E1';
                        e.currentTarget.style.background = '#F1F5F9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#E2E8F0';
                        e.currentTarget.style.background = '#FAFAFC';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '15px' }}>{meta.icon}</span>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: isSelected ? meta.color : '#1E293B' }}>
                          {meta.label}
                        </span>
                      </div>
                      <span
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          border: isSelected ? `5px solid ${meta.color}` : '2px solid #CBD5E1',
                          background: '#FFFFFF',
                          transition: 'all 0.15s ease',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        lineHeight: '1.4',
                        color: isSelected ? '#334155' : '#64748B',
                        marginTop: '2px',
                      }}
                    >
                      {meta.desc}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Row 4: Account Active Status Switch */}
          <div
            style={{
              padding: '14px 16px',
              borderRadius: '12px',
              background: form.isActive ? '#F0FDF4' : '#FEF2F2',
              border: `1.5px solid ${form.isActive ? '#BBF7D0' : '#FECACA'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.25s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>{form.isActive ? '🟢' : '🔴'}</span>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '800', color: form.isActive ? '#166534' : '#991B1B' }}>
                  {form.isActive ? 'Account is Active' : 'Account is Suspended'}
                </div>
                <div style={{ fontSize: '11.5px', color: form.isActive ? '#15803D' : '#B91C1C', marginTop: '1px' }}>
                  {form.isActive
                    ? 'User can authenticate, drill down on reports, and receive mapped email flows.'
                    : 'User login is blocked and distribution dispatches to this account are paused.'}
                </div>
              </div>
            </div>

            {/* iOS-Style Toggle Switch */}
            <div
              onClick={() => setForm({ ...form, isActive: !form.isActive })}
              style={{
                width: '46px',
                height: '26px',
                borderRadius: '99px',
                background: form.isActive ? '#10B981' : '#CBD5E1',
                padding: '3px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#FFFFFF',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                  transform: form.isActive ? 'translateX(20px)' : 'translateX(0)',
                  transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
            </div>
          </div>

          {/* Modal Footer Actions */}
          <div
            style={{
              marginTop: '4px',
              paddingTop: '16px',
              borderTop: '1px solid #F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                border: '1.5px solid #CBD5E1',
                background: '#FFFFFF',
                color: '#475569',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#F8FAFC')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#FFFFFF')}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 22px',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)',
                color: '#FFFFFF',
                fontSize: '13.5px',
                fontWeight: '800',
                letterSpacing: '0.02em',
                cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 6px 18px rgba(0, 82, 204, 0.35)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!saving) e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                if (!saving) e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {saving ? (
                <>
                  <div
                    style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#FFFFFF',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite',
                    }}
                  />
                  <span>Saving Changes…</span>
                </>
              ) : (
                <>
                  <span>{isEditing ? '💾 Save Changes' : '✨ Create User'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClientProvisionModal({ onClose, onSave }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('ALL');
  const [search, setSearch] = useState('');

  const loadUnprovisioned = async () => {
    setLoading(true);
    try {
      const res = await usersApi.unprovisionedClients();
      setClients(res.data);
    } catch {
      toast.error('Failed to load unprovisioned clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnprovisioned();
  }, []);

  const handleProvision = async (client) => {
    setProvisioning(client.email);
    try {
      await usersApi.provisionClient({
        email: client.email,
        name: client.name,
        brand: client.brand,
      });
      toast.success(`Account provisioned for ${client.name} (${client.email})`);
      setClients((prev) => prev.filter((c) => c.email !== client.email));
      if (onSave) onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Provisioning failed');
    } finally {
      setProvisioning(null);
    }
  };

  const brands = ['ALL', ...Array.from(new Set(clients.map((c) => c.brand).filter(Boolean))).sort()];

  const filtered = clients.filter((c) => {
    if (selectedBrand !== 'ALL' && c.brand !== selectedBrand) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.brand && c.brand.toLowerCase().includes(q))
      );
    }
    return true;
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(8px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.2s ease',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '780px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35)',
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            background: 'linear-gradient(135deg, #FAF5FF 0%, #F5F3FF 100%)',
            borderBottom: '1px solid #EDE9FE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: '#7C3AED',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
              }}
            >
              🤝
            </div>
            <div>
              <div style={{ fontSize: '18px', fontWeight: '900', color: '#1E1B4B' }}>
                OEM Client Dispatches — External Contacts
              </div>
              <div style={{ fontSize: '12.5px', color: '#6B21A8', fontWeight: '500' }}>
                External vendor recipients in Email Flows without portal accounts ({clients.length} pending)
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              background: '#EDE9FE',
              color: '#6B21A8',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '16px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Filter Controls */}
        <div
          style={{
            padding: '14px 24px',
            borderBottom: '1px solid #F1F5F9',
            background: '#F8FAFC',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor name, email or brand..."
            style={{
              flex: 1,
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              fontSize: '13px',
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', maxWidth: '380px', paddingBottom: '2px' }}>
            {brands.map((b) => (
              <button
                key={b}
                onClick={() => setSelectedBrand(b)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '16px',
                  fontSize: '11.5px',
                  fontWeight: '700',
                  border: selectedBrand === b ? '1px solid #7C3AED' : '1px solid #E2E8F0',
                  background: selectedBrand === b ? '#7C3AED' : '#FFFFFF',
                  color: selectedBrand === b ? '#FFFFFF' : '#475569',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        {/* Client List */}
        <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>Loading unprovisioned clients...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
              🎉 All external clients in this category have been provisioned!
            </div>
          ) : (
            filtered.map((c) => (
              <div
                key={c.email}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '50%',
                      background: '#FAF5FF',
                      border: '1px solid #D8B4FE',
                      color: '#7C3AED',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '800',
                      fontSize: '13px',
                    }}
                  >
                    {c.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#0F172A' }}>{c.name}</span>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: '800',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          background: '#FAF5FF',
                          color: '#7C3AED',
                          border: '1px solid #E9D5FF',
                        }}
                      >
                        {c.brand}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                      {c.email} • Flows: {c.flows.join(', ')}
                    </div>
                  </div>
                </div>

                <button
                  disabled={provisioning === c.email}
                  onClick={() => handleProvision(c)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#7C3AED',
                    color: '#FFFFFF',
                    fontWeight: '700',
                    fontSize: '12px',
                    cursor: provisioning === c.email ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 6px rgba(124, 58, 237, 0.2)',
                  }}
                >
                  {provisioning === c.email ? 'Creating…' : '✨ Provision Login'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [unprovisionedCount, setUnprovisionedCount] = useState(0);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await usersApi.list(params);
      setUsers(res.data.users);

      usersApi.unprovisionedClients()
        .then((cRes) => setUnprovisionedCount(cRes.data.length))
        .catch(() => {});
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleToggleStatus = async (user) => {
    const actionText = user.isActive ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${actionText} user "${user.name}"?`)) return;
    try {
      if (user.isActive) {
        await usersApi.delete(user.id);
        toast.success(`User "${user.name}" deactivated`);
      } else {
        await usersApi.update(user.id, { isActive: true });
        toast.success(`User "${user.name}" reactivated`);
      }
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${actionText.toLowerCase()} user`);
    }
  };

  const initials = (name) =>
    name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';

  const activeCount = users.filter((u) => u.isActive).length;
  const adminCount = users.filter((u) => u.role === 'ADMIN').length;
  const managerCount = users.filter((u) => u.role === 'MANAGER').length;
  const viewerCount = users.filter((u) => u.role === 'VIEWER').length;

  return (
    <>
      {showModal && (
        <UserModal
          user={editUser}
          onClose={() => {
            setShowModal(false);
            setEditUser(null);
          }}
          onSave={loadUsers}
        />
      )}

      {showClientModal && (
        <ClientProvisionModal
          onClose={() => setShowClientModal(false)}
          onSave={loadUsers}
        />
      )}

      {/* Page Header */}
      <div className="page-header" style={{ paddingBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="page-title" style={{ fontSize: '22px', fontWeight: '900', color: '#0F172A', letterSpacing: '-0.02em' }}>
                Users & Access Governance
              </div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '800',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  background: '#EFF6FF',
                  color: '#0052CC',
                  border: '1px solid #BFDBFE',
                }}
              >
                {users.length} Total Accounts
              </span>
            </div>
            <div className="page-subtitle" style={{ fontSize: '13px', color: '#64748B', marginTop: '4px' }}>
              Manage team accounts, role-based security clearance, and report distribution permissions
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => setShowClientModal(true)}
              style={{
                padding: '10px 16px',
                borderRadius: '10px',
                background: '#FAF5FF',
                border: '1.5px solid #D8B4FE',
                color: '#7C3AED',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '800',
                fontSize: '13.5px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(124, 58, 237, 0.08)',
                transition: 'all 0.15s ease',
              }}
              title="Review and provision external client accounts from email flows"
            >
              <span>🤝</span>
              <span>OEM Clients ({unprovisionedCount})</span>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => {
                setEditUser(null);
                setShowModal(true);
              }}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)',
                boxShadow: '0 6px 18px rgba(0, 82, 204, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '800',
                fontSize: '13.5px',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Team Member
            </button>
          </div>
        </div>

        {/* Metric Badges Strip */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
            marginTop: '18px',
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase' }}>
                Active Accounts
              </div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#10B981', marginTop: '2px' }}>
                {activeCount} <span style={{ fontSize: '12px', fontWeight: '600', color: '#94A3B8' }}>/ {users.length}</span>
              </div>
            </div>
            <span style={{ fontSize: '22px' }}>🟢</span>
          </div>

          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase' }}>
                Administrators
              </div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#EA580C', marginTop: '2px' }}>
                {adminCount}
              </div>
            </div>
            <span style={{ fontSize: '22px' }}>👑</span>
          </div>

          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase' }}>
                Managers & Leads
              </div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#1D4ED8', marginTop: '2px' }}>
                {managerCount}
              </div>
            </div>
            <span style={{ fontSize: '22px' }}>👔</span>
          </div>

          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase' }}>
                Team Viewers
              </div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#15803D', marginTop: '2px' }}>
                {viewerCount}
              </div>
            </div>
            <span style={{ fontSize: '22px' }}>👤</span>
          </div>
        </div>
      </div>

      {/* Page Body */}
      <div className="page-body">
        {/* Search & Filter Toolbar */}
        <div className="toolbar" style={{ marginBottom: '16px', display: 'flex', gap: '10px' }}>
          <div className="search-box" style={{ flex: 1 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              placeholder="Search users by name, email, or department…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="form-input"
            style={{
              width: '180px',
              borderRadius: '10px',
              border: '1.5px solid #CBD5E1',
              fontWeight: '600',
              fontSize: '13px',
              background: '#FFFFFF',
            }}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles ({users.length})</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r} ({users.filter((u) => u.role === r).length})
              </option>
            ))}
          </select>
        </div>

        {/* Data Card & Table */}
        <div className="card" style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #E2E8F0' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748B', fontWeight: '600' }}>
                Loading accounts...
              </div>
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  <th style={{ padding: '12px 18px', fontSize: '11.5px', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>
                    User Profile
                  </th>
                  <th style={{ padding: '12px 18px', fontSize: '11.5px', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>
                    Role & Scope
                  </th>
                  <th style={{ padding: '12px 18px', fontSize: '11.5px', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px 18px', fontSize: '11.5px', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>
                    Registered
                  </th>
                  <th style={{ padding: '12px 18px', fontSize: '11.5px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', textAlign: 'right' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const roleMeta = ROLE_META[u.role] || ROLE_META.VIEWER;
                  return (
                    <tr
                      key={u.id}
                      style={{
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#F8FAFC')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* User Column */}
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{
                              width: '38px',
                              height: '38px',
                              borderRadius: '12px',
                              background: `linear-gradient(135deg, ${roleMeta.color} 0%, #0F172A 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              fontWeight: '800',
                              color: 'white',
                              flexShrink: 0,
                              boxShadow: `0 3px 8px ${roleMeta.color}30`,
                            }}
                          >
                            {initials(u.name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '13.5px', color: '#0F172A' }}>
                              {u.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748B', fontFamily: 'monospace' }}>
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Role Column */}
                      <td style={{ padding: '12px 18px' }}>
                        <span
                          style={{
                            fontSize: '11.5px',
                            fontWeight: '800',
                            padding: '3px 9px',
                            borderRadius: '20px',
                            background: roleMeta.bg,
                            color: roleMeta.color,
                            border: `1px solid ${roleMeta.border}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <span>{roleMeta.icon}</span>
                          <span>{roleMeta.label}</span>
                        </span>
                      </td>

                      {/* Status Column */}
                      <td style={{ padding: '12px 18px' }}>
                        <span
                          style={{
                            fontSize: '11.5px',
                            padding: '3px 10px',
                            borderRadius: '99px',
                            fontWeight: '800',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            background: u.isActive ? '#F0FDF4' : '#FEF2F2',
                            color: u.isActive ? '#15803D' : '#DC2626',
                            border: `1px solid ${u.isActive ? '#BBF7D0' : '#FECACA'}`,
                          }}
                        >
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: u.isActive ? '#10B981' : '#EF4444',
                            }}
                          />
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Registered Date Column */}
                      <td style={{ padding: '12px 18px', color: '#64748B', fontSize: '12.5px' }}>
                        {u.createdAt ? formatDistanceToNow(new Date(u.createdAt), { addSuffix: true }) : '—'}
                      </td>

                      {/* Actions Column */}
                      <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setEditUser(u);
                              setShowModal(true);
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '700',
                              background: '#EFF6FF',
                              color: '#0052CC',
                              border: '1px solid #BFDBFE',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#DBEAFE';
                              e.currentTarget.style.borderColor = '#93C5FD';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#EFF6FF';
                              e.currentTarget.style.borderColor = '#BFDBFE';
                            }}
                          >
                            ✏️ Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => handleToggleStatus(u)}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '700',
                              background: u.isActive ? '#FEF2F2' : '#F0FDF4',
                              color: u.isActive ? '#DC2626' : '#15803D',
                              border: `1px solid ${u.isActive ? '#FECACA' : '#BBF7D0'}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = u.isActive ? '#FEE2E2' : '#DCFCE7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = u.isActive ? '#FEF2F2' : '#F0FDF4';
                            }}
                          >
                            {u.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748B' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>👤</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#1E293B' }}>No users found</div>
              <div style={{ fontSize: '13px', marginTop: '4px' }}>Try adjusting your search criteria or role filter.</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
